import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { AgentDot } from '../../AgentDot';
import { MaestroMark } from '../MaestroMark';
import { Badge, Card, Typography, type CardVariant } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { existingAgents } from '../../../lib/agent-card-groups';
import { useMyAgents } from '../../../lib/my-agents';
import {
  AGENT_NODE_SIZE,
  AGENT_RING_R,
  GENOME_SIZE,
  MAX_AGENT_NODES,
  RING_RADII,
  polarToOffset,
  radialFlightOffset,
  shortSkillLabel,
  skillSlots,
  slotHalf,
  slotSide,
  type SkillSlot,
} from '../../../lib/genome-layout';
import {
  AGENT_NODE_LAND_MS,
  AGENT_STAGGER_MS,
  REEL_START_MS,
  RING_SWEEP_STAGGER_MS,
  SKILL_DOT_FLIGHT_MS,
  SKILL_DOT_STAGGER_MS,
} from '../../../lib/genome-timing';
import { ORBIT_ONCE_MS, TOP_UP_LEAD_MS, joinNodeDelayMs, joinReelTimeline } from '../../../lib/agent-join-timing';
import styles from './AgentJoinCard.module.css';

// The in-chat "an agent joined your team" moment — rendered in place of an assistant
// bubble for a RUN of consecutive messages carrying `metadata.agentCard` (the fold lives
// in `lib/agent-card-groups`, so a burst of hires is ONE card, never a cascade).
//
// It is the SAME genome the Your Agents hero draws — same three dashed rings, same skill
// dots, same agent nodes, the Maestro at the centre — because it is the same object: the
// team, with someone new on it. Geometry comes from `lib/genome-layout` and the beats from
// `lib/genome-timing` / `lib/agent-join-timing`, all shared with the hero and with the
// native `AgentJoinCard` (iOS `Features/Chat/AgentJoinCard.swift`, Android
// `features/chat/components/AgentJoinCard.kt`).
//
// What the CARD does that the hero doesn't:
//   · the team that was already there lands FIRST, one by one, and only once it has
//     settled do the new hire(s) get their own beat — the "who was here vs. who just
//     joined" sequence, rather than a ring that was silently already full,
//   · the header is a rolling SPOTLIGHT on whichever node landed last (its name, and its
//     role as a pill), falling back to "Your Team · N" until the first one lands, with the
//     team's running skills total underneath,
//   · the settled ring keeps orbiting for as long as the card is on screen,
//   · a hire that folds into an already-mounted run flies out ONLY its own node — the rest
//     of the figure never replays.
//
// The reel plays every time the card appears, including scroll-back, matching native: this
// is a moment you should be able to see whenever you look at it.
//
// Two deliberate web differences, both inherited from the hero rather than invented here:
// skill names are a HOVER affordance instead of native's flash-during-assembly beat (the
// web design draws the dots bare — see `lib/genome-timing`), and the Maestro wears web's
// `MaestroMark` rather than native's older disc-and-notch. The card is passive — no tap to
// replay, no node selection — matching iOS; Android still carries both.

export type AgentCardAgent = { id?: string; name: string; title?: string };

/** With no real skills to draw, the constellation still needs a shape (native's fallback dot count). */
const DECORATIVE_SKILL_DOTS = 8;

type ReelMode = 'pending' | 'reel' | 'settled';

interface Cohort {
  /** Nodes present when the reel started — anything past this arrived later and gets its own lead-in. */
  mountCount: number;
  existingCount: number;
  timeline: ReturnType<typeof joinReelTimeline>;
  startedAt: number;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function AgentJoinCard({
  agents,
  /**
   * The Card skin. `sunken` (white) as the standalone in-chat card; a caller that NESTS this figure
   * inside its own card passes `stone`, the recessed skin the DS Cards rule asks for there — see the
   * band card.
   */
  variant = 'sunken',
  className,
  /**
   * Whether the settled roster lands first. Web's spelling of native's `existingAgents` argument, which
   * a call site can hand an empty array: off, the figure is exactly the agents passed in. The BAND card
   * turns it off because that card is about THIS group landing, not about where they sit in a wider
   * roster.
   */
  showExistingTeam = true,
  /**
   * Drawn INSIDE another card that already names what landed — today the band card, which is titled
   * with the band's name and lists every member under the figure. It drops the SPOTLIGHT header, which
   * names whichever node landed last and so made the whole card read as a card about that one agent.
   * The figure and its reel are untouched: the animation is the whole reason a host embeds this.
   * Native calls the same flag `embedded`; the nested SKIN is `variant` here, because the DS Cards rule
   * owns that choice on web.
   */
  embedded = false,
}: {
  agents: AgentCardAgent[];
  variant?: CardVariant;
  className?: string;
  showExistingTeam?: boolean;
  embedded?: boolean;
}) {
  const { t } = useTranslation();
  const team = useMyAgents();
  const maskId = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  // The roster and skills as they were when this card first had them, frozen. The nodes are
  // addressed by POSITION into `[...existing, ...joining]`, and the roster refetches moments
  // after a hire — by then it already contains the agent joining here, which would shift what
  // every decided index refers to. Native freezes it at mount for the same reason.
  //
  // A caller that does NOT want the settled roster does not wait for it either. The reel is gated on
  // this snapshot, so waiting on a read the figure never uses leaves the host card holding an EMPTY
  // BOX until the network answers — visible as a grey hole where the band's celebration should be, and
  // permanent if that read never settles. The skills only set the decorative dot count, which already
  // falls back when empty.
  const snapshot = useRef<{ existing: AgentCardAgent[]; skills: string[] } | null>(null);
  if (!snapshot.current && (team.status !== 'loading' || !showExistingTeam)) {
    const roster = team.status === 'ready' ? team.team.agents : [];
    const skills = team.status === 'ready' ? team.team.skills : [];
    snapshot.current = {
      // The new hires always draw; the settled team fills whatever is left of the ring, so a big
      // roster crowds neither the figure nor the moment it exists to show. Sliced HERE, not per
      // render: a later hire must never shorten this list, or every decided index shifts under it.
      existing: showExistingTeam
        ? existingAgents(roster, agents)
            .map((a) => ({ id: a.id, name: a.name, title: a.title }))
            .slice(0, Math.max(MAX_AGENT_NODES - agents.length, 0))
        : [],
      skills: skills.map((s) => s.name).filter(Boolean),
    };
  }

  const existing = snapshot.current?.existing ?? [];
  const allAgents = [...existing, ...agents];
  const nodeCount = allAgents.length;
  const skillNames = snapshot.current?.skills ?? [];
  const slots = skillSlots(skillNames.length ? skillNames : Array<string>(DECORATIVE_SKILL_DOTS).fill(''));

  const [mode, setMode] = useState<ReelMode>('pending');
  const cohort = useRef<Cohort | null>(null);
  // Per-node landing delay. The mount cohort's are absolute from the reel start; a node that
  // folded in later is measured from its own arrival, so its neighbours never move.
  const delays = useRef<number[]>([]);
  const [landed, setLanded] = useState(0);
  const landedCount = useRef(0);

  // Nothing assembles until the team read settles: the reel is worth more played over the real
  // roster than played early over an empty ring.
  useEffect(() => {
    if (mode !== 'pending' || !snapshot.current) return;
    cohort.current = {
      mountCount: nodeCount,
      existingCount: existing.length,
      timeline: joinReelTimeline(existing.length, agents.length),
      startedAt: Date.now(),
    };
    setMode(prefersReducedMotion() ? 'settled' : 'reel');
  }, [mode, nodeCount, existing.length, agents.length]);

  if (cohort.current && delays.current.length < nodeCount) {
    const { mountCount, existingCount, timeline } = cohort.current;
    const from = delays.current.length;
    for (let i = from; i < nodeCount; i++) {
      delays.current.push(
        i < mountCount
          ? joinNodeDelayMs(i, existingCount, timeline)
          : TOP_UP_LEAD_MS + (i - Math.max(from, mountCount)) * AGENT_STAGGER_MS,
      );
    }
  }

  // The spotlight follows the landings, so the header names whoever just arrived.
  useEffect(() => {
    if (mode === 'settled') {
      landedCount.current = nodeCount;
      setLanded(nodeCount);
      return;
    }
    if (mode !== 'reel' || !cohort.current) return;
    const { mountCount, startedAt } = cohort.current;
    const elapsed = Date.now() - startedAt;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = landedCount.current; i < nodeCount; i++) {
      const delay = delays.current[i] ?? 0;
      // A mount-cohort node's delay is absolute, so a re-run mid-reel must not restart its clock.
      const wait = Math.max(i < mountCount ? delay - elapsed : delay, 0);
      timers.push(
        setTimeout(() => {
          landedCount.current = Math.max(landedCount.current, i + 1);
          setLanded(landedCount.current);
        }, wait),
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [mode, nodeCount]);

  const spotlight = landed > 0 ? allAgents[Math.min(landed, nodeCount) - 1] : undefined;
  const role = spotlight?.title?.trim();
  const joinedLabel =
    agents.length === 1
      ? t('chat.agentCard.joined', { name: agents[0].name })
      : t('chat.agentCard.joinedMany', { count: agents.length });

  return (
    <Card variant={variant} className={[styles.card, className].filter(Boolean).join(' ')} role="status" aria-label={joinedLabel}>
      {!embedded && (
        <div className={styles.head}>
          <div className={styles.headText}>
            <Typography variant="h4" color="strong">
              {spotlight ? spotlight.name : `${t('agents.team')} · ${nodeCount}`}
            </Typography>
            {skillNames.length > 0 && (
              <Typography variant="p3" color="secondary">
                {t(skillNames.length === 1 ? 'agents.skillsCountOne' : 'agents.skillsCount', {
                  count: skillNames.length,
                })}
              </Typography>
            )}
          </div>
          {role && <Badge variant="agent-profession" label={role} className={styles.role} />}
        </div>
      )}

      <div className={styles.stageBox}>
        <div
          className={`${styles.stage} ${styles[mode]}`}
          style={
            {
              '--orbit-at': `${cohort.current?.timeline.orbitAtMs ?? 0}ms`,
              '--orbit-dur': `${ORBIT_ONCE_MS}ms`,
            } as CSSProperties
          }
        >
          <svg
            className={styles.rings}
            viewBox={`0 0 ${GENOME_SIZE} ${GENOME_SIZE}`}
            aria-hidden
            focusable="false"
          >
            <defs>
              {RING_RADII.map((r, i) => (
                <mask
                  key={r}
                  id={`${maskId}-ring-${i}`}
                  maskUnits="userSpaceOnUse"
                  maskContentUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width={GENOME_SIZE}
                  height={GENOME_SIZE}
                >
                  {/* One mask dash grows from nothing to the full circumference — SwiftUI's
                      `trim(to:)` expressed in SVG. */}
                  <circle
                    className={styles.ringMask}
                    cx={GENOME_SIZE / 2}
                    cy={GENOME_SIZE / 2}
                    r={r}
                    pathLength={1}
                    style={{ animationDelay: `${REEL_START_MS + i * RING_SWEEP_STAGGER_MS}ms` }}
                  />
                </mask>
              ))}
            </defs>
            {RING_RADII.map((r, i) => (
              <circle
                key={r}
                className={styles.ring}
                cx={GENOME_SIZE / 2}
                cy={GENOME_SIZE / 2}
                r={r}
                mask={`url(#${maskId}-ring-${i})`}
              />
            ))}
          </svg>

          <div className={styles.layer}>
            {slots.map((slot) => (
              <SkillDot key={slot.index} slot={slot} />
            ))}
          </div>

          <MaestroMark className={styles.maestro} />

          {/* The whole ring is what orbits, so a node never spins about its own centre. */}
          <div className={styles.layer + ' ' + styles.orbit}>
            {allAgents.map((agent, i) => {
              const key = agent.id || agent.name;
              return (
                <span
                  key={`${key}:${i}`}
                  className={styles.node}
                  style={
                    {
                      ...polarToOffset(AGENT_RING_R, (360 / Math.max(nodeCount, 1)) * i),
                      animationDelay: `${delays.current[i] ?? 0}ms`,
                      animationDuration: `${AGENT_NODE_LAND_MS}ms`,
                    } as CSSProperties
                  }
                >
                  <AgentDot id={key} size={AGENT_NODE_SIZE} />
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}

// A skill's dot: the 10px mark inside a 40px box, its name on hover — the web genome's
// replacement for native's flash-during-assembly label beat.
function SkillDot({ slot }: { slot: SkillSlot }) {
  const { dx, dy } = radialFlightOffset(slot.r, slot.angleDeg);
  return (
    <span
      className={styles.skillDot}
      data-side={slotSide(slot.angleDeg)}
      data-half={slotHalf(slot.angleDeg)}
      style={
        {
          ...polarToOffset(slot.r, slot.angleDeg),
          '--fly-dx': `${dx}px`,
          '--fly-dy': `${dy}px`,
          animationDelay: `${REEL_START_MS + slot.index * SKILL_DOT_STAGGER_MS}ms`,
          animationDuration: `${SKILL_DOT_FLIGHT_MS}ms`,
        } as CSSProperties
      }
    >
      <span className={styles.skillMark} aria-hidden />
      {slot.name && <span className={styles.skillLabel}>{shortSkillLabel(slot.name)}</span>}
    </span>
  );
}
